'use client';
import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useEffect, useMemo, useState, } from 'react';
import { toRootCssVariables } from '../../personly-libs-css/src/index.js';
import { useCookies } from './cookies.js';
const setCssRootVariables = (cssVariables) => {
    const rootCssVariables = toRootCssVariables(cssVariables);
    const root = document.querySelector(':root');
    for (const { key, value } of rootCssVariables) {
        root.style.setProperty(`--${key}`, value);
    }
};
const reduceCssRootVariables = (cssVariables) => cssVariables.reduce((acc, cur) => (Object.assign(Object.assign({}, acc), { [cur.key]: cur.value })), {});
const SchemesContext = createContext(null);
const SchemesProvider = ({ children, schemes, cookieName = 'schemeHandle', hasWindow = Boolean(typeof window !== 'undefined' && window), }) => {
    const { getCookie, setCookie } = useCookies();
    const [scheme, setScheme] = useState(schemes.scheme);
    const [cookieScheme, setCookieScheme] = useState(null);
    const schemeValid = useCallback((scheme) => schemes.schemes.map(({ id }) => id).includes(scheme), [schemes.schemes]);
    useEffect(() => {
        const initialCookieScheme = getCookie(cookieName);
        if (initialCookieScheme && schemeValid(initialCookieScheme)) {
            // eslint-disable-next-line react-hooks/set-state-in-effect
            setCookieScheme(initialCookieScheme);
        }
    }, [schemeValid, getCookie, cookieName]);
    const updateScheme = useCallback((scheme) => {
        if (scheme === 'SYSTEM') {
            const browserSchemes = schemes.schemes.filter(({ flags }) => flags.browser);
            for (const browserScheme of browserSchemes) {
                const media = `(prefers-color-scheme: ${browserScheme.id.toLowerCase()})`;
                if (window.matchMedia(media).matches) {
                    setCssRootVariables(browserScheme.cssVariables);
                    setScheme(browserScheme.id);
                    break;
                }
            }
        }
        else {
            const foundScheme = schemes.schemes.find(({ id }) => id === scheme);
            if (foundScheme) {
                const { cssVariables } = foundScheme;
                setCssRootVariables(cssVariables);
                setScheme(scheme);
            }
        }
    }, [schemes.schemes]);
    const setSchemeHandle = useCallback((scheme) => {
        if (schemeValid(scheme)) {
            setCookie('schemeHandle', scheme);
            setCookieScheme(scheme);
        }
    }, [setCookie, schemeValid]);
    useEffect(() => {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        if (cookieScheme)
            updateScheme(cookieScheme);
    }, [cookieScheme, updateScheme]);
    const cssVariables = useMemo(() => {
        if (scheme === 'SYSTEM') {
            let cssVariables = [];
            if (hasWindow) {
                const browserSchemes = schemes.schemes.filter(({ flags }) => flags.browser);
                for (const browserScheme of browserSchemes) {
                    const media = `(prefers-color-scheme: ${browserScheme.id.toLowerCase()})`;
                    if (window.matchMedia(media).matches) {
                        cssVariables = browserScheme.cssVariables;
                        break;
                    }
                }
            }
            else {
                const foundScheme = schemes.schemes.find(({ flags }) => flags.browser);
                if (foundScheme) {
                    const { cssVariables } = foundScheme;
                    return reduceCssRootVariables(cssVariables);
                }
            }
            return reduceCssRootVariables(cssVariables);
        }
        else {
            const foundScheme = schemes.schemes.find(({ id }) => id === scheme);
            if (foundScheme) {
                const { cssVariables } = foundScheme;
                return reduceCssRootVariables(cssVariables);
            }
        }
        /* c8 ignore next 2 */
        return {};
    }, [schemes, scheme, hasWindow]);
    const schemeHandle = useMemo(() => cookieScheme || scheme, [cookieScheme, scheme]);
    const displaySchemeHandle = useMemo(() => (schemeHandle === 'SYSTEM' ? schemes.schemes[0].id : schemeHandle), [schemeHandle, schemes.schemes]);
    const value = useMemo(() => ({
        schemeHandle,
        setSchemeHandle,
        cssVariables,
        displaySchemeHandle,
    }), [setSchemeHandle, schemeHandle, cssVariables, displaySchemeHandle]);
    return (_jsx(SchemesContext.Provider, { value: value, children: children }));
};
SchemesProvider.propTypes = {};
function useSchemes() {
    const context = useContext(SchemesContext);
    if (!context)
        throw new Error('useSchemes requires a context!');
    return context;
}
export { SchemesProvider, useSchemes };
//# sourceMappingURL=schemes.js.map